import { Component, OnInit } from '@angular/core';
import { Router, NavigationCancel, NavigationEnd } from '@angular/router';
import {
    Location,
    LocationStrategy,
    PathLocationStrategy,
} from '@angular/common';
import { filter } from 'rxjs/operators';
import { ConfigAppService } from '@services/shared/config-app.service';
import { SpinnerService } from '@services/shared/spinner.service';
import { InscripcionTraceService } from '@services/shared/inscripcion-trace.service';
import { AvisoSuspensionService } from '@services/shared/aviso-suspension.service';
import { AuthService } from '@services/auth/auth.service';
import { AuditoriaAccion } from '@enum/auditoria/auditoria-keys.enum';
import { VERSION_TIMESTAMP } from '../version';
declare let $: any;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [
        Location,
        {
            provide: LocationStrategy,
            useClass: PathLocationStrategy,
        },
    ],
})
export class AppComponent implements OnInit {
    location: any;
    routerSubscription: any;

    constructor(
        private router: Router,
        private configAppService: ConfigAppService,
        public spinnerService: SpinnerService,
        private inscripcionTrace: InscripcionTraceService,
        private authService: AuthService,
        private avisoSuspension: AvisoSuspensionService,
    ) {}

    async ngOnInit() {
        this.registrarCatchGlobal();

        const ok = await this.configAppService.cargarConfiguracion();

        if (!ok) {
            console.warn('Configuración no válida o incompleta');
        }

        this.checkVersionAndClearCache();
        this.recallJsFuntions();

        // Pinta el título de la pestaña al cargar: la navegación inicial ya está
        // resuelta y el NavigationEnd inicial se pierde al suscribir tarde.
        this.actualizarTituloPestania();

        // Revalidación real de sesión, una sola vez al abrir la app.
        void this.validarSesionAlArranque();

        // Último paso, para no competir con la navegación inicial: si alguien
        // ya tenía la sesión abierta, el cartel de sistema suspendido también
        // le aparece. Sólo perfiles afiliados (misma regla que el redirect).
        void this.avisoSuspension.mostrarSiCorresponde({ soloAfiliados: true });
    }

    /**
     * Valida la sesión contra el backend al arranque (cuando el usuario abre el
     * navegador y entra a Signo). Si el token está vencido o la sesión fue
     * cerrada, manda al login temprano. Si el backend no responde (red, 500,
     * timeout) NO bloquea: deja entrar y se reintenta en la próxima petición.
     */
    private async validarSesionAlArranque(): Promise<void> {
        if (!this.authService.hasToken()) {
            return;
        }

        const resultado = await this.authService.revalidarSesion();

        if (resultado === 'invalid') {
            this.authService.handleAuthFailure();
            return;
        }

        // Landing inicial: si la app arranca en la raíz, los perfiles
        // administrativos van al Panel. Es ONE-SHOT (solo al bootstrap): una
        // vez cargada la app, ir a Inicio es acción del usuario y no redirige.
        const url = (this.router.url || '').split('?')[0];
        if (url === '/' || url === '') {
            void this.authService.redirigirSegunPerfil();
        }
    }

    /**
     * Captura errores globales (promesas rechazadas y errores de ventana) que
     * de otro modo se pierden en silencio en producción.
     */
    private registrarCatchGlobal(): void {
        window.addEventListener('unhandledrejection', (event: any) => {
            try {
                this.inscripcionTrace.logError(
                    AuditoriaAccion.GLOBAL_ERROR,
                    event?.reason || new Error('Unhandled promise rejection'),
                    { paso: 'unhandledrejection' },
                );
            } catch {}
        });

        window.addEventListener('error', (event: any) => {
            try {
                this.inscripcionTrace.logError(
                    AuditoriaAccion.GLOBAL_ERROR,
                    event?.error || new Error(event?.message || 'window error'),
                    { paso: 'window.onerror' },
                );
            } catch {}
        });
    }

    private checkVersionAndClearCache() {
        const storedVersion = localStorage.getItem('app_version');

        if (storedVersion !== VERSION_TIMESTAMP) {
            console.log('Nueva versión detectada. Limpiando caché...');
            this.clearCacheAndReload();
        }
    }

    private clearCacheAndReload() {
        // 1. Limpiar caché del navegador (Service Workers / Cache API)
        if ('caches' in window) {
            caches.keys().then((cacheNames) => {
                cacheNames.forEach((cacheName) => {
                    caches.delete(cacheName);
                });
            });
        }

        // 2. Preservar sesión y configuración crítica
        const sessionKeys = [
            'signo-token',
            'signo-identificador',
            'identificacion',
            'email',
            'IDIOMA',
            'EMAIL_KEY',
            'app_version'
        ];
        
        const preserved = {};
        sessionKeys.forEach(key => {
            const val = localStorage.getItem(key);
            if (val) preserved[key] = val;
        });

        // 3. Limpiar localStorage
        localStorage.clear();

        // 4. Restaurar valores preservados
        Object.keys(preserved).forEach(key => {
            localStorage.setItem(key, preserved[key] as string);
        });

        // Asegurar que la versión se actualice incluso si no estaba preservada
        localStorage.setItem('app_version', VERSION_TIMESTAMP);

        // 5. Forzar recarga completa desde el servidor
        window.location.reload();
    }

    recallJsFuntions() {
        this.routerSubscription = this.router.events
            .pipe(
                filter(
                    (event) =>
                        event instanceof NavigationEnd ||
                        event instanceof NavigationCancel,
                ),
            )
            .subscribe((event) => {
                this.location = this.router.url;
                if (!(event instanceof NavigationEnd)) {
                    return;
                }
                window.scrollTo(0, 0);
                this.actualizarTituloPestania();
            });
    }

    /**
     * Título de la pestaña según el `data.title` de la ruta activa.
     * Ej: { title: 'Modificar Usuario' } -> "Modificar Usuario - Signo Médico".
     */
    private actualizarTituloPestania(): void {
        let route = this.router.routerState.snapshot.root;
        while (route.firstChild) {
            route = route.firstChild;
        }
        const title = route.data ? route.data['title'] : undefined;
        document.title = title ? `${title} - Signo Médico` : 'Signo Médico';
    }
}
